import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { auth } from "@/api/firebase";
import { db } from "@/api/firestore";
import { createProtoFromFirestore } from "@/lib/proto";
import { ScheduleSchema } from "@generated/proto/model";
import type { Schedule } from "@generated/proto/model";

interface ScheduleRow {
  id: string;
  proto: Schedule;
  created?: Date;
  modified?: Date;
}

export function useScheduleFirestore(tripId?: string) {
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tripId) {
      setSchedules([]);
      setLoading(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Create Firestore query for schedules ordered by creation time (newest first)
    const schedulesRef = collection(
      db,
      `users/${user.uid}/trips/${tripId}/schedules`
    );
    const q = query(schedulesRef, orderBy("created", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          const data = snapshot.docs.map((doc) => {
            const raw: any = doc.data();
            const proto = createProtoFromFirestore<Schedule>(ScheduleSchema, raw);
            return {
              id: doc.id,
              proto,
              created: raw.created?.toDate?.(),
              modified: raw.modified?.toDate?.(),
            } satisfies ScheduleRow;
          });
          setSchedules(data);
          setLoading(false);
        } catch (err: any) {
          setError("Failed to load schedules");
          console.error("[useScheduleFirestore] error parsing schedules:", err);
          setLoading(false);
        }
      },
      (err) => {
        setError("Firestore subscription failed");
        console.error("[useScheduleFirestore] subscription error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tripId]);

  return {
    schedules,
    loading,
    error,
  };
}